'use client';

import {
  createBackerRewardScope,
  useActiveWallet,
  useAuth,
  useClaimBackerFees,
  useGetMarketBackerEarnings,
  useTurnkeySigningDeps,
  useWallets,
} from '@morfi/backend-sdk-v2/client';
import { isMarketResolved } from '@morfi/contract-sdk';
import type { BackerRewardsPool } from '@/components/market/backers/backer-earnings';
import { D6_SCALE, D9_SCALE } from '@/config/solana';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';

const D9_SCALE_BIGINT: bigint = BigInt(D9_SCALE);

interface UseBackerRewardsParams {
  marketPubkey: string;
  marketStatus: string;
  enabled?: boolean;
}

interface UseBackerRewardsResult {
  rewardPool: BackerRewardsPool | null;
  /** Whether viewer identity or its wallet-scoped reward row is still loading. */
  isPending: boolean;
  /** Vault total in display units (D6 decoded). Always populated. */
  vaultAmount: number;
  /** Card headline: live vault before claim, lifetime pool after a full claim. */
  headlineAmount: number;
  /** Lifetime fees collected (D6 decoded). Always populated. */
  totalCollected: number;
  /** D6 string of vault for AnimatedNumber tick-up. */
  vaultD6: string;
  /** User's pro-rata earnings in display units. 0 if not a backer. */
  earnedAmount: number;
  /** D6 string of earned for AnimatedNumber. */
  earnedD6: string;
  /** User's share-token balance (D6). 0 if not a backer. */
  userShareD6: string;
  /** Cumulative claimed amount (D6 decoded). 0 if no claims. */
  claimedTotal: number;
  /** Current token share, or claimed payout / lifetime pool after a full claim. */
  userSharePct: number;
  /** Whether the user currently holds or has already claimed backer rewards. */
  isBacker: boolean;
  /** Whether the user has already claimed any backer rewards for this market. */
  hasClaimed: boolean;
  /** Claim enabled — unclaimed shares + market is resolved/graduated. */
  canClaim: boolean;
  /** Whether the claim mutation is currently in flight. */
  isClaiming: boolean;
  /** Fire the claim — burns the user's entire balance. */
  claim: () => void;
}

function d6ToDisplay(value: string | undefined): number {
  if (value === undefined) return 0;
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n / D6_SCALE;
}

/**
 * Orchestrates all backer-rewards state for the market detail page.
 *
 * Combines `useGetMarketBackerEarnings` (vault, supply, userShare, claimed)
 * with `useClaimBackerFees` (mutation) and the market status predicate
 * (`isMarketResolved`) into a single set of display-ready values.
 * Components consuming this hook do not need to call any SDK hook
 * directly. Vault total + total collected are always populated so the
 * card renders the marketing surface even for non-backers.
 */
export function useBackerRewards({
  marketPubkey,
  marketStatus,
  enabled = true,
}: UseBackerRewardsParams): UseBackerRewardsResult {
  const { user, isLoading: isAuthLoading } = useAuth();
  const { wallets } = useWallets();
  const {
    state: multiWalletState,
    enabled: multiWalletEnabled,
    primaryWallet,
  } = useMultiWalletFeature();
  const accountWalletPubkeys = wallets.map((wallet) => wallet.address);
  const primaryWalletPubkey =
    primaryWallet !== null &&
    primaryWallet.archivedAt === null &&
    primaryWallet.isAvailable !== false
      ? primaryWallet.address
      : null;
  const walletPubkeys = multiWalletEnabled
    ? accountWalletPubkeys
    : primaryWalletPubkey === null
      ? []
      : [primaryWalletPubkey];
  const earnings = useGetMarketBackerEarnings(
    {
      pubkey: marketPubkey,
      ...(multiWalletEnabled || primaryWalletPubkey === null
        ? {}
        : { wallet: primaryWalletPubkey }),
      rewardScope: createBackerRewardScope(user?.id, walletPubkeys),
      walletPubkeysCsv: walletPubkeys.join(','),
      fields: [
        'vaultD6',
        'supplyD6',
        'totalCollectedD6',
        'userShareD6',
        'earnedD6',
        'claimedTotalD6',
        'canClaim',
        'marketState',
      ],
    },
    { enabled },
  );
  const claim = useClaimBackerFees();
  const { activeWallet } = useActiveWallet();
  const signingDeps = useTurnkeySigningDeps();

  const globalRow = earnings.data;
  const viewerRow = multiWalletEnabled || primaryWalletPubkey !== null ? globalRow : undefined;
  const vaultD6 = globalRow?.vaultD6 ?? '0';
  const supplyD6 = globalRow?.supplyD6 ?? '0';
  const userShareD6 = viewerRow?.userShareD6 ?? '0';
  const currentEarnedD6 = viewerRow?.earnedD6 ?? '0';
  const totalCollectedD6 = globalRow?.totalCollectedD6 ?? '0';
  const claimedTotalD6 = viewerRow?.claimedTotalD6 ?? '0';

  const ZERO = BigInt(0);
  const userShareBn = (() => {
    try {
      return BigInt(userShareD6);
    } catch {
      // SILENT-CATCH: defensive fallback when the SDK returns a non-numeric
      // string for userShareD6 — treat as zero stake rather than crashing the
      // entire rewards card; not actionable so no error reporting.
      return ZERO;
    }
  })();
  const claimedTotalBn = (() => {
    try {
      return BigInt(claimedTotalD6);
    } catch {
      // SILENT-CATCH: same defensive fallback as above for claimedTotalD6.
      return ZERO;
    }
  })();
  const supplyBn = (() => {
    try {
      return BigInt(supplyD6);
    } catch {
      // SILENT-CATCH: same defensive fallback as above for supplyD6.
      return ZERO;
    }
  })();
  const totalCollectedBn = (() => {
    try {
      return BigInt(totalCollectedD6);
    } catch {
      // SILENT-CATCH: same defensive fallback as above for totalCollectedD6.
      return ZERO;
    }
  })();

  const viewerStatePending = isAuthLoading || multiWalletState === 'pending' || earnings.isPending;
  const hasUnclaimedShares = userShareBn > ZERO;
  const hasClaimed = claimedTotalBn > ZERO;
  const shareNumerator = hasUnclaimedShares ? userShareBn : claimedTotalBn;
  const shareDenominator = hasUnclaimedShares ? supplyBn : totalCollectedBn;
  const userSharePpb =
    shareDenominator === ZERO ? ZERO : (shareNumerator * D9_SCALE_BIGINT) / shareDenominator;
  const userSharePct = Number(userSharePpb) / D9_SCALE;
  const isBacker = hasUnclaimedShares || hasClaimed;
  const canClaim =
    enabled && !viewerStatePending && hasUnclaimedShares && isMarketResolved(marketStatus);
  const earnedD6 = hasUnclaimedShares ? currentEarnedD6 : claimedTotalD6;
  const headlineD6 = hasClaimed && !hasUnclaimedShares ? totalCollectedD6 : vaultD6;

  const fire = (): void => {
    if (!canClaim || activeWallet === null || activeWallet === undefined) return;
    claim.mutate({
      marketAddress: marketPubkey,
      burnAmountD6: userShareD6,
      activeWallet: {
        id: activeWallet.id,
        address: activeWallet.address,
        type: activeWallet.type,
      },
      signingDeps,
    });
  };

  return {
    rewardPool:
      globalRow?.vaultD6 === undefined || globalRow?.supplyD6 === undefined || earnings.isError
        ? null
        : { vaultD6, supplyD6 },
    isPending: viewerStatePending,
    vaultAmount: d6ToDisplay(vaultD6),
    headlineAmount: d6ToDisplay(headlineD6),
    totalCollected: d6ToDisplay(totalCollectedD6),
    vaultD6,
    earnedAmount: d6ToDisplay(earnedD6),
    earnedD6,
    userShareD6,
    claimedTotal: d6ToDisplay(claimedTotalD6),
    userSharePct,
    isBacker,
    hasClaimed,
    canClaim,
    isClaiming: claim.isPending,
    claim: fire,
  };
}
